import { t } from "@lingui/macro";
import graphql from "babel-plugin-relay/macro";
import { Image } from "react-native";
import { useFragment } from "react-relay";

import { Button } from "@whoosh/design-system/src/components/controls";
import { EyeClosedIcon } from "@whoosh/design-system/src/components/icons_v2/general";
import {
  Box,
  HStack,
  Text,
} from "@whoosh/design-system/src/components/primitive";
import { getThumbnail } from "@whoosh/utilities/src/Cloudinary";

import { ClubRow_MemberProfile$key } from "./__generated__/ClubRow_MemberProfile.graphql";
import { useUpdateMemberProfileHidden } from "./hooks/useUpdateMemberProfileHidden";

type ClubRowProps = {
  fragmentRef: ClubRow_MemberProfile$key;
};

export const ClubRow = ({ fragmentRef }: ClubRowProps) => {
  const data = useFragment(ClubRowFragmentString, fragmentRef);
  const club = data.club;

  const [updateMemberProfileHidden, isUpdatingMemberProfile] =
    useUpdateMemberProfileHidden();

  const toggleVisibility = () => {
    updateMemberProfileHidden({
      hidden: !data.hidden,
      memberProfileId: data.id,
    });
  };

  return (
    <Box width="100%">
      <HStack
        gap={4}
        py={5}
        alignItems="center"
        justifyContent="space-between"
        pr={{ base: 6, md: 0 }}
      >
        <HStack gap={4} alignItems="center">
          <Image
            source={{
              uri: getThumbnail(club?.logoImage?.publicId, 56, 56)?.toURL(),
            }}
            accessibilityLabel={
              club?.name ? t`${club.name} logo` : t`Club Logo`
            }
            style={{ borderRadius: 8, width: 56, height: 56 }}
          />
          <Text variant="mainBaseMedium">{club?.name}</Text>
        </HStack>
        <Button
          size="tiny"
          onPress={toggleVisibility}
          isDisabled={isUpdatingMemberProfile}
          leftIcon={<EyeClosedIcon />}
          variant={data.hidden ? "ghost" : "secondary"}
          accessibilityLabel={t`Show button`}
        >
          {data.hidden ? t`Show` : t`Hide`}
        </Button>
      </HStack>
      <Box width="100%" height={1} bg="surface-dividers" />
    </Box>
  );
};

const ClubRowFragmentString = graphql`
  fragment ClubRow_MemberProfile on MemberProfile {
    id
    hidden
    club {
      id
      slug
      name
      logoImage {
        # TODO(ENG-3436): Remove the publicId alias when legacy image props are retired.
        publicId: renderingKey
      }
    }
    member {
      ... on Member {
        id
      }
      ... on Customer {
        id
      }
    }
  }
`;
